/**
 * @license Copyright (c) 2003-2021, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

/**
 * @module iframe-embed/iframeembedediting
 */

import { Plugin, icons } from 'ckeditor5/src/core';
import { ButtonView } from 'ckeditor5/src/ui';
import { toWidget } from 'ckeditor5/src/widget';
import { logWarning, createElement } from 'ckeditor5/src/utils';

import InsertIframeEmbedCommand from './insertiframeembedcommand';
import UpdateIframeEmbedCommand from './updateiframeembedcommand';

import '../theme/lark.css';
import '../theme/iframeembed.css';

/**
 * The iframe embed editing feature.
 *
 * @extends module:core/plugin~Plugin
 */
export default class IframeEmbedEditing extends Plugin {
	/**
	 * @inheritDoc
	 */
	static get pluginName() {
		return 'IframeEmbedEditing';
	}

	/**
	 * @inheritDoc
	 */
	constructor( editor ) {
		super( editor );

		editor.config.define( 'iframeEmbed', {
			showPreviews: false,
			sanitizeHtml: iframeHtml => {
				/**
				 * When using the iframe embed feature with the `iframeEmbed.showPreviews=true` option, it is strongly recommended to
				 * define a sanitize function that will clean up the input iframe in order to avoid XSS vulnerability.
				 *
				 * For a detailed overview, check the {@glink features/iframe-embed iframe embed feature} documentation.
				 *
				 * @error iframe-embed-provide-sanitize-function
				 */
				logWarning( 'iframe-embed-provide-sanitize-function' );

				return {
					html: iframeHtml,
					hasChanged: false
				};
			}
		} );
	}

	/**
	 * @inheritDoc
	 */
	init() {
		const editor = this.editor;
		const schema = editor.model.schema;

		schema.register( 'iframe', {
			isObject: true,
			allowWhere: '$block',
			allowAttributes: [ 'src', 'title', 'height' ]
		} );

		editor.commands.add(
			'updateIframeEmbed',
			new UpdateIframeEmbedCommand( editor )
		);
		editor.commands.add(
			'insertIframeEmbed',
			new InsertIframeEmbedCommand( editor )
		);

		this._setupConversion();
	}

	/**
	 * Prepares converters for the feature.
	 *
	 * @private
	 */
	_setupConversion() {
		const editor = this.editor;
		const t = editor.t;
		const view = editor.editing.view;

		const iframeEmbedConfig = editor.config.get('iframeEmbed');

		editor.conversion.for('upcast').elementToElement({
			view: {
				name: 'figure',
				classes: 'iframe-embed'
			},
			model: (viewElement, { writer }) => {
				// The figure.iframe-embed is registered as a raw content element,
				// so all it's content is available in a custom property.
				return writer.createElement('iframe', {
					src: viewElement.getChild(0).getAttribute('src'),
					height: viewElement.getChild(0).getAttribute('height'),
					title: viewElement.getChild(0).getAttribute('title')
				});

				
			}
		});

		editor.conversion.for( 'dataDowncast' ).elementToElement({
			model: 'iframe',
			view: (modelElement, { writer }) => {
				const url = modelElement.getAttribute('src') || '';
				const height = modelElement.getAttribute('height') || '';
				const title = modelElement.getAttribute('title') || '';
				const embedElement = writer.createContainerElement('figure', {
					class: 'iframe-embed'
				});

				writer.insert(
					writer.createPositionAt(embedElement, 0),
					writer.createRawElement('iframe', {
						src: url,
						height: height,
						title: title
					})
				);

				return embedElement;
			}
		});

		editor.conversion.for( 'editingDowncast' ).elementToElement( {
			triggerBy: {
				attributes: ['src', 'height', 'title']
			},
			model: 'iframe',
			view: (modelElement, { writer }) => {
				let domContentWrapper, state, props;

				const viewContainer = writer.createContainerElement( 'figure', {
					class: 'iframe-embed',
					'data-iframe-embed-label': t( 'Embed iframe' ),
					dir: editor.locale.uiLanguageDirection
				} );

				viewContainer._setAttribute('height', modelElement._attrs.get('height'));
				viewContainer._setAttribute('src', modelElement._attrs.get('src'));
				viewContainer._setAttribute('title', modelElement._attrs.get('title'));

				// Widget cannot be a raw element because the widget system would not be able
				// to add its UI to it. Thus, we need this wrapper.
				const viewContentWrapper = writer.createRawElement(
					'div',
					{
						class: 'iframe-embed__content-wrapper'
					},
					function( domElement ) {
						domContentWrapper = domElement;

						renderContent( { domElement, editor, state, props } );

						// Since there is a `data-cke-ignore-events` attribute set on the wrapper element in the editable mode,
						// the explicit `mousedown` handler on the `capture` phase is needed to move the selection onto the whole
						// iframe embed widget.
						domContentWrapper.addEventListener(
							'mousedown',
							() => {
								if ( state.isEditable ) {
									const model = editor.model;
									const selectedElement =
										model.document.selection.getSelectedElement();

									// Move the selection onto the whole iframe embed widget if it's currently not selected.
									if ( selectedElement !== modelElement ) {
										model.change( writer =>
											writer.setSelection( modelElement, 'on' )
										);
									}
								}
							},
							true
						);
					}
				);

				// API exposed on each raw iframe embed widget so other features can control a particular widget.
				const iframeApi = {
					makeEditable() {
						state = Object.assign( {}, state, {
							isEditable: true
						} );

						renderContent( {
							domElement: domContentWrapper,
							editor,
							state,
							props
						} );

						view.change( writer => {
							writer.setAttribute(
								'data-cke-ignore-events',
								'true',
								viewContentWrapper
							);
						} );

						// This could be potentially pulled to a separate method called focusTextarea().
						domContentWrapper.querySelector( 'input' ).focus();
					},
					save( values ) {
						// If the value didn't change, we just cancel. If it changed,
						// it's enough to update the model – the entire widget will be reconverted.
						
						if ( values !== state.getRawHtmlUrlValue() ) {
							
							editor.execute( 'updateIframeEmbed', values );
							editor.editing.view.focus();
							//this.cancel();

						} else {
							this.cancel();
						}
					},
					cancel() {
						state = Object.assign( {}, state, {
							isEditable: false
						} );

						renderContent( {
							domElement: domContentWrapper,
							editor,
							state,
							props
						} );
						editor.editing.view.focus();

						view.change( writer => {
							writer.removeAttribute(
								'data-cke-ignore-events',
								viewContentWrapper
							);
						} );
					}
				};

				state = {
					showPreviews: iframeEmbedConfig.showPreviews,
					isEditable: false,
					getRawHtmlUrlValue: () => modelElement.getAttribute( 'src' ) || '',
					getRawHtmlHeightValue: () => modelElement.getAttribute( 'height' ) || '',
					getRawHtmlTitleValue: () => modelElement.getAttribute( 'title' ) || '',
				};

				props = {
					sanitizeHtml: iframeEmbedConfig.sanitizeHtml,
					textareaPlaceholder: t( 'URL address' ),
					attributeHeightPlaceholder: t( 'Height' ),
					attributeTitlePlaceholder: t( 'Title' ),

					onEditClick() {
						iframeApi.makeEditable();
					},
					onSaveClick(values) {
						iframeApi.save( values );
					},
					onCancelClick() {
						iframeApi.cancel();
					}
				};

				writer.insert(
					writer.createPositionAt( viewContainer, 0 ),
					viewContentWrapper
				);

				writer.setCustomProperty( 'iframeApi', iframeApi, viewContainer );
				writer.setCustomProperty( 'iframe', true, viewContainer );

				return toWidget( viewContainer, writer, {
					widgetLabel: t( 'Embed iframe' ),
					hasSelectionHandle: true
				} );
			}
		} );

		function renderContent( { domElement, editor, state, props } ) {
			// Remove all children;
			domElement.textContent = '';
			const domDocument = domElement.ownerDocument;

			if (state.showPreviews && ! state.isEditable) {
				domElement.append(
					createPreviewContainer({
						domDocument,
						state,
						props: {
							sanitizeHtml: props.sanitizeHtml
						},
						editor
					})
				);

				return;
			}

			let nodeUrl = createNodeUrlInput({domDocument, state, props: {
				isDisabled: ! state.isEditable,
				placeholder: props.textareaPlaceholder
			}});
			domElement.append(nodeUrl);

			let nodeHeight = createNodeHeightInput({domDocument, state, props: {
				isDisabled: ! state.isEditable,
				placeholder: props.attributeHeightPlaceholder
			}});
			domElement.append(nodeHeight);

			let nodeTitle = createNodeTitleInput({domDocument, state, props: {
				isDisabled: ! state.isEditable,
				placeholder: props.attributeTitlePlaceholder
			}});
			domElement.append(nodeTitle);

			domElement.prepend(
				createNodeButtonsWrapper({editor, domDocument, state, props: {
					onEditClick: props.onEditClick,
					onSaveClick: () => {
						props.onSaveClick({
							url: nodeUrl.value,
							height: nodeHeight.value,
							title: nodeTitle.value
						});
					},
					onCancelClick: props.onCancelClick
				}})
			);
		}

		function createNodeButtonsWrapper( { editor, domDocument, state, props } ) {
			const nodeButtonsWrapper = createElement( domDocument, 'div', {
				class: 'iframe-embed__buttons-wrapper'
			} );
			const nodeEditButton = createNodeButton( editor, 'edit' );
			const nodeSaveButton = createNodeButton( editor, 'save' );
			const nodeCancelButton = createNodeButton( editor, 'cancel' );

			if ( state.isEditable ) {
				const clonedDomSaveButton = nodeSaveButton.cloneNode( true );
				const clonedDomCancelButton = nodeCancelButton.cloneNode( true );

				clonedDomSaveButton.addEventListener( 'click', evt => {
					evt.preventDefault();
					props.onSaveClick();
				} );

				clonedDomCancelButton.addEventListener( 'click', evt => {
					evt.preventDefault();
					props.onCancelClick();
				} );

				nodeButtonsWrapper.appendChild( clonedDomSaveButton );
				nodeButtonsWrapper.appendChild( clonedDomCancelButton );
			} else {
				const clonedDomEditButton = nodeEditButton.cloneNode( true );

				clonedDomEditButton.addEventListener( 'click', evt => {
					evt.preventDefault();
					props.onEditClick();
				} );

				nodeButtonsWrapper.appendChild( clonedDomEditButton );
			}

			return nodeButtonsWrapper;
		}

		function createNodeUrlInput( { domDocument, state, props } ) {
			const node = createElement( domDocument, 'input', {
				type: 'url',
				placeholder: props.placeholder,
				class: 'ck ck-reset ck-input ck-input-text iframe-embed__source'
			} );

			node.disabled = props.isDisabled;
			node.value = state.getRawHtmlUrlValue();

			return node;
		}

		function createNodeHeightInput( { domDocument, state, props } ) {
			const node = createElement( domDocument, 'input', {
				type: 'text',
				placeholder: props.placeholder,
				class: 'ck ck-reset ck-input ck-input-text iframe-embed__source'
			} );

			node.disabled = props.isDisabled;
			node.value = state.getRawHtmlHeightValue();

			return node;
		}

		function createNodeTitleInput( { domDocument, state, props } ) {
			const node = createElement( domDocument, 'input', {
				type: 'text',
				placeholder: props.placeholder,
				class: 'ck ck-reset ck-input ck-input-text iframe-embed__source'
			} );

			node.disabled = props.isDisabled;
			node.value = state.getRawHtmlTitleValue();

			return node;
		}

		function createPreviewContainer( { domDocument, state, props, editor } ) {
			const sanitizedOutput = props.sanitizeHtml( state.getRawHtmlUrlValue() );
			const placeholderText =
				state.getRawHtmlUrlValue().length > 0 ?
					t( 'No preview available' ) :
					t( 'Empty embed content' );

			const domPreviewPlaceholder = createElement(
				domDocument,
				'div',
				{
					class: 'ck ck-reset_all iframe-embed__preview-placeholder'
				},
				placeholderText
			);

			const domPreviewContent = createElement( domDocument, 'div', {
				class: 'iframe-embed__preview-content',
				dir: editor.locale.contentLanguageDirection
			} );

			domPreviewContent.innerHTML = `<iframe src="${ sanitizedOutput.html }"></iframe>`;

			return createElement(
				domDocument,
				'div',
				{
					class: 'iframe-embed__preview'
				},
				[ domPreviewPlaceholder, domPreviewContent ]
			);
		}
	}
}

// Returns a toggle mode button DOM element that can be cloned and used in conversion.
//
//	@param {module:utils/locale~Locale} locale Editor locale.
//	@param {'edit'|'save'|'cancel'} type Type of button to create.
//	@returns {iframeElement}
function createNodeButton( editor, type ) {
	const t = editor.locale.t;
	const buttonView = new ButtonView( editor.locale );
	const command = editor.commands.get( 'updateIframeEmbed' );

	buttonView.set( {
		tooltipPosition: editor.locale.uiLanguageDirection === 'rtl' ? 'e' : 'w',
		icon: icons.pencil,
		tooltip: true
	} );

	buttonView.render();

	if ( type === 'edit' ) {
		buttonView.set( {
			icon: icons.pencil,
			label: t( 'Edit link' ),
			class: 'iframe-embed__edit-button'
		} );
	} else if ( type === 'save' ) {
		buttonView.set( {
			icon: icons.check,
			label: t( 'Save changes' ),
			class: 'iframe-embed__save-button'
		} );
		buttonView.bind( 'isEnabled' ).to( command, 'isEnabled' );
	} else {
		buttonView.set( {
			icon: icons.cancel,
			label: t( 'Cancel' ),
			class: 'iframe-embed__cancel-button'
		} );
	}

	buttonView.destroy();

	return buttonView.element.cloneNode( true );
}
